import { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { CompatRoute } from "react-router-dom-v5-compat";

import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";
import loaderWithRetry from "seneca-common/utils/loaderWithRetry";

import lazyWithPreload from "features/app/components/lazyWithPreload";
import { linkToCoursesPage } from "features/dashboard/pages/courses-page/links";
import { ROUTES } from "routing/routes";

import { linkToCourseExamQuestionsMiniMockMarking } from "./course-exam-questions-mini-mock-marking/links";
import { EqSessionContextProvider } from "./course-exam-questions/EQSessionContext/EqSessionContext";
import { linkToCourseExamQuestionsSession } from "./course-exam-questions/links";
import { linkToCourseHomepage } from "./course-homepage/links";
import { linkToClassroomSection } from "./section/links";

const RecommendedSessionStarterRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "recommend-section-chunk" */ "./recommend-section/Route"
      )
  )
);

const SectionRoute = lazyWithPreload(() =>
  loaderWithRetry(
    () => import(/* webpackChunkName: "section-chunk" */ "./section/Route")
  )
);

const CourseExamQuestionsSessionRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-exam-questions-session-chunk" */ "./course-exam-questions/Route"
      )
  )
);

const CourseExamQuestionsMockSessionRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-exam-questions-mock-session-chunk" */ "./course-exam-questions-mini-mock-session/Route"
      )
  )
);

const CourseExamQuestionsMiniMockMarkingRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-exam-questions-mock-marking-chunk" */ "./course-exam-questions-mini-mock-marking/Route"
      )
  )
);

const CourseExamQuestionsMiniMockResultsRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-exam-questions-mock-results-chunk" */ "./course-exam-questions-mini-mock-results/Route"
      )
  )
);

const CourseExamQuestionsHomepageRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-exam-questions-homepage-chunk" */ "./course-exam-questions-homepage/Route"
      )
  )
);

const CourseAssignmentsSubRouter = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-assignments-sub-router-chunk" */ "./assignments/routes/SubRouter"
      )
  )
);

const CourseAskAmeliaRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-ask-amelia-chunk" */ "./ask-amelia/Route"
      )
  )
);

const CourseVocabularyRoute = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-vocabulary-chunk" */ "./vocabulary/Route"
      )
  )
);

const CourseHomepageRoute = lazyWithPreload(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "course-homepage-chunk" */ "./course-homepage/Route"
      )
  )
);

const MultiSectionSubRouter = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "multi-section-route-chunk" */ "./multi-section/routes/SubRouter"
      )
  )
);

export default function CourseSubRouter() {
  useEffectOnMount(() => {
    SectionRoute.preload();
    CourseHomepageRoute.preload();
  });
  return (
    <EqSessionContextProvider>
      <Suspense>
        <Switch>
          <CompatRoute
            exact
            path={ROUTES.CLASSROOM.COURSE.RECOMMENDED_SESSION.path}
          >
            <RecommendedSessionStarterRoute />
          </CompatRoute>
          <Route path={linkToClassroomSection()}>
            <SectionRoute />
          </Route>
          <Route path={linkToCourseExamQuestionsSession()}>
            <CourseExamQuestionsSessionRoute />
          </Route>
          <CompatRoute
            path={ROUTES.CLASSROOM.COURSE.EXAM_QUESTIONS.MINI_MOCK.SESSION.path}
          >
            <CourseExamQuestionsMockSessionRoute />
          </CompatRoute>
          <CompatRoute
            path={ROUTES.CLASSROOM.COURSE.EXAM_QUESTIONS.MINI_MOCK.RESULTS.path}
          >
            <CourseExamQuestionsMiniMockResultsRoute />
          </CompatRoute>
          <Route path={linkToCourseExamQuestionsMiniMockMarking()}>
            <CourseExamQuestionsMiniMockMarkingRoute />
          </Route>
          <CompatRoute path={ROUTES.CLASSROOM.COURSE.EXAM_QUESTIONS.path}>
            <CourseExamQuestionsHomepageRoute />
          </CompatRoute>
          <CompatRoute path={ROUTES.CLASSROOM.COURSE.ASSIGNMENTS.path}>
            <CourseAssignmentsSubRouter />
          </CompatRoute>
          <CompatRoute path={ROUTES.CLASSROOM.COURSE.MULTI_SECTION.path}>
            <MultiSectionSubRouter />
          </CompatRoute>
          <CompatRoute path={ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.path}>
            <CourseAskAmeliaRoute />
          </CompatRoute>
          <CompatRoute path={ROUTES.CLASSROOM.COURSE.VOCABULARY.path}>
            <CourseVocabularyRoute />
          </CompatRoute>
          <Route path={linkToCourseHomepage()}>
            <CourseHomepageRoute />
          </Route>
          <Redirect to={linkToCoursesPage()} />
        </Switch>
      </Suspense>
    </EqSessionContextProvider>
  );
}
